import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * The frame's eyedropper (Huanrui, 2026-10-06): any colour shown on the
 * board, picked by clicking it.
 *
 * Done here rather than with the browser's own EyeDropper, which samples the
 * screen through the operating system and showed the wrong colour in its
 * magnifier on Huanrui's machine. This reads the colour from what the page
 * itself draws, so what is shown is what is picked:
 *
 *   - a picture: the pixel of the picture under the pointer, read from the
 *     picture file (turned and cropped as the board shows it);
 *   - anything drawn on a canvas -- the Edit area windows among them;
 *   - anything else: its background colour, the nearest one that is not
 *     see-through -- a frame, a card, the board's paper.
 *
 * A circle beside the pointer shows the colour and its code. Click to take
 * it; Escape, or a right click, to leave.
 */

type Rgb = [number, number, number]

const pictures = new Map<string, Promise<HTMLCanvasElement | null>>()

/** A picture's pixels, read once per picture from its file. */
function pixelsOf(src: string): Promise<HTMLCanvasElement | null> {
  let held = pictures.get(src)
  if (!held) {
    held = fetch(src)
      .then((response) => (response.ok ? response.blob() : Promise.reject(new Error(String(response.status)))))
      .then((blob) => createImageBitmap(blob))
      .then((bitmap) => {
        const canvas = document.createElement('canvas')
        canvas.width = bitmap.width
        canvas.height = bitmap.height
        canvas.getContext('2d', { willReadFrequently: true })?.drawImage(bitmap, 0, 0)
        bitmap.close()
        return canvas
      })
      .catch(() => null)
    pictures.set(src, held)
  }
  return held
}

function parseCss(colour: string): { rgb: Rgb; alpha: number } | null {
  const m = /rgba?\(\s*([\d.]+)[ ,]+([\d.]+)[ ,]+([\d.]+)(?:[ ,/]+([\d.]+%?))?\s*\)/.exec(colour)
  if (!m) return null
  const a = m[4] === undefined ? 1 : m[4].endsWith('%') ? Number(m[4].slice(0, -1)) / 100 : Number(m[4])
  return { rgb: [Number(m[1]), Number(m[2]), Number(m[3])], alpha: a }
}

const hexOf = ([r, g, b]: Rgb) =>
  `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`

/** The pixel of a picture on the board under the pointer, as the board shows it. */
async function fromPicture(img: HTMLImageElement, x: number, y: number): Promise<Rgb | null> {
  const src = img.currentSrc || img.src
  if (!src) return null
  const canvas = await pixelsOf(src)
  if (!canvas) return null
  const r = img.getBoundingClientRect()
  const w = img.offsetWidth
  const h = img.offsetHeight
  if (!w || !h) return null
  // Turned on the board: undo the turn about the picture's centre.
  const turn = Number(/rotate\((-?[\d.]+)deg\)/.exec(img.style.transform)?.[1] ?? 0)
  const rad = (-turn * Math.PI) / 180
  const dx = x - (r.left + r.width / 2)
  const dy = y - (r.top + r.height / 2)
  const ux = dx * Math.cos(rad) - dy * Math.sin(rad)
  const uy = dx * Math.sin(rad) + dy * Math.cos(rad)
  // The board's zoom, from how big the picture is drawn against its own size.
  const zoom = Math.sqrt((r.width * r.height) / (w * h)) || 1
  const lx = ux / zoom + w / 2
  const ly = uy / zoom + h / 2
  // object-fit: cover.
  const nw = canvas.width
  const nh = canvas.height
  const s = Math.max(w / nw, h / nh)
  const px = Math.floor((lx - (w - nw * s) / 2) / s)
  const py = Math.floor((ly - (h - nh * s) / 2) / s)
  if (px < 0 || py < 0 || px >= nw || py >= nh) return null
  const d = canvas.getContext('2d', { willReadFrequently: true })?.getImageData(px, py, 1, 1).data
  return d && d[3]! > 0 ? [d[0]!, d[1]!, d[2]!] : null
}

function fromCanvas(el: HTMLCanvasElement, x: number, y: number): Rgb | null {
  try {
    const r = el.getBoundingClientRect()
    const px = Math.floor(((x - r.left) * el.width) / Math.max(1, r.width))
    const py = Math.floor(((y - r.top) * el.height) / Math.max(1, r.height))
    const d = el.getContext('2d')?.getImageData(px, py, 1, 1).data
    return d && d[3]! > 0 ? [d[0]!, d[1]!, d[2]!] : null
  } catch {
    // A canvas holding something from another site cannot be read.
    return null
  }
}

async function colourAt(x: number, y: number, skip: Element | null): Promise<Rgb | null> {
  for (const el of document.elementsFromPoint(x, y)) {
    if (skip && skip.contains(el)) continue
    if (el instanceof HTMLCanvasElement) {
      const c = fromCanvas(el, x, y)
      if (c) return c
      continue
    }
    if (el instanceof HTMLImageElement) {
      const c = await fromPicture(el, x, y)
      if (c) return c
      continue
    }
    const bg = parseCss(getComputedStyle(el).backgroundColor)
    if (bg && bg.alpha >= 0.5) return bg.rgb
  }
  return null
}

export function ColourPicker({ onPick, onCancel }: { onPick: (hex: string) => void; onCancel: () => void }) {
  const layer = useRef<HTMLDivElement | null>(null)
  const [at, setAt] = useState<{ x: number; y: number } | null>(null)
  const [colour, setColour] = useState<string | null>(null)
  const asked = useRef(0)

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      onCancel()
    }
    window.addEventListener('keydown', key, true)
    return () => window.removeEventListener('keydown', key, true)
  }, [onCancel])

  const sample = (x: number, y: number) => {
    const ticket = (asked.current += 1)
    void colourAt(x, y, layer.current).then((rgb) => {
      // Only the latest question's answer counts; a picture read slowly does not jump back.
      if (ticket === asked.current) setColour(rgb ? hexOf(rgb) : null)
    })
  }

  return createPortal(
    <div
      ref={layer}
      className="cpick"
      onPointerMove={(event) => {
        setAt({ x: event.clientX, y: event.clientY })
        sample(event.clientX, event.clientY)
      }}
      onPointerDown={(event) => {
        event.preventDefault()
        event.stopPropagation()
        if (event.button !== 0) return onCancel()
        const ticket = (asked.current += 1)
        void colourAt(event.clientX, event.clientY, layer.current).then((rgb) => {
          if (ticket !== asked.current) return
          if (rgb) onPick(hexOf(rgb))
          else onCancel()
        })
      }}
      onContextMenu={(event) => event.preventDefault()}
      onWheel={(event) => event.stopPropagation()}
    >
      {at ? (
        <div className="cpick__loupe" style={{ left: at.x + 18, top: at.y + 18 }}>
          <span className="cpick__swatch" style={{ background: colour ?? 'transparent' }} />
          <span className="cpick__hex">{colour ?? '—'}</span>
        </div>
      ) : null}
    </div>,
    document.body,
  )
}
